import { columns } from "@/components/financial-disclosures/financial-disclosures.columns";
import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useFinancialDisclosuresModal } from "@/hooks/useFinancialDisclosuresModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { FinancialDisclosuresModalForm } from "./financial-disclosures-modal-form";

const financialDisclosureFormSchema = z.object({
  personName: z.string().min(1, "Person name is required"),
  year: z.number().int().min(1900).max(2100),
  disclosureType: z.enum(["campaign", "conflict-of-interest"]),
  office: z.string().min(1, "Office is required"),
  filingDate: z.date(),
  documentMediaId: z.string().optional(),
});

type FinancialDisclosureFormValues = z.infer<
  typeof financialDisclosureFormSchema
>;

export function FinancialDisclosuresTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: disclosures = [], isLoading } = useQuery(
    trpc.financialDisclosures.list.queryOptions(),
  );

  const deleteId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);

  const isModalOpen = useFinancialDisclosuresModal((s) => s.isOpen);
  const setIsModalOpen = useFinancialDisclosuresModal((s) => s.setIsModalOpen);
  const setDisclosureId = useFinancialDisclosuresModal(
    (s) => s.setDisclosureId,
  );
  const disclosureId = useFinancialDisclosuresModal((s) => s.disclosureId);
  const defaultValues = useFinancialDisclosuresModal((s) => s.defaultValues);
  const setDefaultValues = useFinancialDisclosuresModal(
    (s) => s.setDefaultValues,
  );

  const form = useForm<FinancialDisclosureFormValues>({
    resolver: zodResolver(financialDisclosureFormSchema),
    defaultValues: {
      personName: "",
      year: new Date().getFullYear(),
      disclosureType: "campaign",
      office: "",
      filingDate: new Date(),
      documentMediaId: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        personName: defaultValues.personName || "",
        year: defaultValues.year || new Date().getFullYear(),
        disclosureType: defaultValues.disclosureType || "campaign",
        office: defaultValues.office || "",
        filingDate: defaultValues.filingDate || new Date(),
        documentMediaId: defaultValues.documentMediaId,
      });
    }
  }, [form, defaultValues]);

  const deleteMutation = useMutation(
    trpc.financialDisclosures.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["financialDisclosures"] });
        toast.success("Financial disclosure deleted successfully");
        setDeleteId(null);
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to delete financial disclosure",
        );
      },
    }),
  );

  const createMutation = useMutation(
    trpc.financialDisclosures.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["financialDisclosures"] });
        toast.success("Financial disclosure created successfully");
        setIsModalOpen(false);
        setDisclosureId(null);
        form.reset();
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to create financial disclosure",
        );
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.financialDisclosures.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["financialDisclosures"] });
        toast.success("Financial disclosure updated successfully");
        setIsModalOpen(false);
        setDisclosureId(null);
        form.reset();
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to update financial disclosure",
        );
      },
    }),
  );

  const handleSubmit = (data: FinancialDisclosureFormValues) => {
    if (disclosureId) {
      updateMutation.mutate({ id: disclosureId, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Financial Disclosures
            </h2>
            <p className="text-muted-foreground">
              Manage campaign financial disclosures and conflict of interest
              forms
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => {
                setDefaultValues({});
                form.reset({
                  personName: "",
                  year: new Date().getFullYear(),
                  disclosureType: "campaign",
                  office: "",
                  filingDate: new Date(),
                  documentMediaId: undefined,
                });
                setIsModalOpen(true);
              }}
            >
              New Disclosure
            </Button>
          </div>
        </div>

        <FinancialDisclosuresModalForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        <ConfirmModal
          open={Boolean(deleteId)}
          onConfirm={() => {
            if (deleteId) deleteMutation.mutate({ id: deleteId });
          }}
          onDismiss={() => {
            setDeleteId(null);
          }}
          isLoading={deleteMutation.isPending}
        />

        {!isLoading && (
          <DataTable
            data={disclosures}
            columns={columns}
            filterColumn="personName"
          />
        )}
      </div>
    </div>
  );
}
